import get from 'lodash.get';
import humps from 'humps';
import JsonApi from 'utils/jsonApi';
import { PAGINATION_LIMIT } from 'commons/constants';
import type { ArticleObject, ArticlesPayload, ArticleMedia, GetBySlugPayload } from 'commons/types';
import { getCriteriaValueFromLandingPageType } from 'screens/Dashboard/LandingPage/utils';

const getArticleTaxonomyCookieValue = (
  taxonomyName: string,
  educationLevel: string | null,
): string => {
  if (taxonomyName !== 'Noodle' && taxonomyName !== null) {
    return taxonomyName;
  }

  switch (educationLevel) {
    case 'UNDERGRAD':
      return 'undergrad';

    case 'GRADUATE_SCHOOL':
      return 'multi_grad';

    case 'K_12':
      return 'k_12';

    case 'PRE_SCHOOL':
      return 'pre_k';

    default:
      return '';
  }
};

export default class Article {
  jsonApi: JsonApi;

  constructor(jsonApi: JsonApi) {
    this.jsonApi = jsonApi;
  }

  async getById(articleId: string): Promise<ArticleObject> {
    const articleResponse = await this.jsonApi.get(`/articles/${articleId}`);
    const camelCaseArticleId = articleId.charAt(0).toLowerCase() + articleId.slice(1);
    const article = get(articleResponse, `entities.managedArticle.${camelCaseArticleId}`, {});
    article.link = get(articleResponse, 'entities.link', '');
    return article;
  }

  async getBySlug({ slug }: GetBySlugPayload): Promise<ArticleObject> {
    const articleResponse = await this.jsonApi.get(`/v2/articles/${slug}`);
    const article = humps.camelizeKeys(articleResponse);
    const { landingPageUrl, taxonomyName = null, educationLevel = null } = article;
    const parsedTaxonomyName = taxonomyName === null ? 'Noodle' : taxonomyName;
    article.link = `/articles/${slug}`;
    article.browseMoreLink = landingPageUrl ? `/articles/l/${landingPageUrl}` : '/articles';
    article.taxonomyName = parsedTaxonomyName;
    article.slug = slug;
    document.cookie = `article_taxonomy_type=${getArticleTaxonomyCookieValue(
      parsedTaxonomyName,
      educationLevel,
    )}`;
    return article;
  }

  async getAll(page: number): Promise<any> {
    const response = await this.jsonApi.get(`/articles?page[number]=${page}&page[size]=10`);
    const articles = get(response, 'entities.article', {});
    const count = get(response, 'entities.count', {});
    return {
      articles,
      count,
    };
  }

  async getArticles({
    page = 1,
    elementsPerPage = PAGINATION_LIMIT,
    criteria,
  }: Record<string, any>): Promise<any> {
    const articleFields =
      'category,education_level,programs,subcategory,title,author_avatar,author_name,author_title,preview_content,media,pub_date,slug,source_id,tags';
    const filters = criteria || '[{"name": "source_type", "op": "eq", "val": "internal"}]';
    const response = await this.jsonApi.get(
      `/articles?filter=${filters}&fields[article]=${articleFields}&page[number]=${page}&page[size]=${elementsPerPage}&image_w=360&author_avatar_w=75&include=tags&sort=-pub_date`,
    );
    const articles = Object.values(get(response, 'entities.managedArticle', {}));
    const count = get(response, 'entities.count', {});
    return {
      articles,
      count,
    };
  }

  async getTaxonomyArticles({
    page = 1,
    elementsPerPage = PAGINATION_LIMIT,
    taxonomyType,
    slug,
    degreeType,
  }: ArticlesPayload): Promise<any> {
    const searchParams = new URLSearchParams();
    searchParams.set(getCriteriaValueFromLandingPageType(String(taxonomyType)), String(slug));
    searchParams.set('skip', String((page - 1) * elementsPerPage));
    searchParams.set('limit', String(elementsPerPage));

    if (degreeType) {
      searchParams.set('degree_type', degreeType);
    }

    const response = await this.jsonApi.get(`/v2/articles?${searchParams.toString()}`, false);
    const articles = get(response, 'data', []);
    const count = get(response, 'total', {});
    return {
      articles,
      count,
    };
  }

  async searchImagesForArticle(searchTerm: string, pageNumber: number): Promise<ArticleMedia[]> {
    const url = `/articles/images?filter=[{"name": "search", "op": "eq", "val": "${searchTerm}"}]&page[number]=${pageNumber}&page[size]=30`;
    const response = await this.jsonApi.get(url);
    const imagesResponse = get(response, 'entities.articleImage', {});

    if (imagesResponse && Object.keys(imagesResponse).length > 0) {
      const coverImages: ArticleMedia[] = Object.values(imagesResponse) as any;
      return coverImages;
    }

    return [];
  }
}
